// =============================================================================
// Photo Gallery — "Load more" pagination + lightbox
// =============================================================================
//
// Markup contract (see aicity/blocks/gallery_block.html.jinja):
//   .gallery[data-gallery][data-items-url][data-total]
//     [data-gallery-grid]            grid receiving the figures
//       a[data-gallery-photo]        href = large image, data-original,
//                                    data-title, data-caption
//     button[data-gallery-more][data-offset]
//       [data-gallery-remaining]
//
// One lightbox element is shared by all galleries on the page.

interface ItemsResponse {
    html: string;
    next_offset: number;
    total: number;
    has_more: boolean;
}

interface Photo {
    large: string;
    original: string;
    title: string;
    caption: string;
}

// -----------------------------------------------------------------------------
// Lightbox
// -----------------------------------------------------------------------------

class Lightbox {
    private root: HTMLElement;
    private img: HTMLImageElement;
    private caption: HTMLElement;
    private counter: HTMLElement;
    private download: HTMLAnchorElement;
    private photos: Photo[] = [];
    private index = 0;
    private lastFocus: Element | null = null;

    constructor() {
        this.root = document.createElement("div");
        this.root.className = "lightbox";
        this.root.hidden = true;
        this.root.setAttribute("role", "dialog");
        this.root.setAttribute("aria-modal", "true");
        this.root.setAttribute("aria-label", "Photo");
        this.root.innerHTML = `
            <div class="lightbox__backdrop" data-lb-close></div>
            <figure class="lightbox__figure">
                <img class="lightbox__image" alt="">
                <figcaption class="lightbox__caption"></figcaption>
            </figure>
            <button type="button" class="lightbox__btn lightbox__btn--close" data-lb-close aria-label="Close">&times;</button>
            <button type="button" class="lightbox__btn lightbox__btn--prev" data-lb-prev aria-label="Previous photo">&#8249;</button>
            <button type="button" class="lightbox__btn lightbox__btn--next" data-lb-next aria-label="Next photo">&#8250;</button>
            <div class="lightbox__bar">
                <span class="lightbox__counter"></span>
                <a class="lightbox__download" href="#" download target="_blank" rel="noopener">Download</a>
            </div>`;
        document.body.appendChild(this.root);

        this.img = this.root.querySelector(".lightbox__image") as HTMLImageElement;
        this.caption = this.root.querySelector(".lightbox__caption") as HTMLElement;
        this.counter = this.root.querySelector(".lightbox__counter") as HTMLElement;
        this.download = this.root.querySelector(".lightbox__download") as HTMLAnchorElement;

        this.root.querySelectorAll("[data-lb-close]").forEach((el) =>
            el.addEventListener("click", () => this.close()),
        );
        (this.root.querySelector("[data-lb-prev]") as HTMLElement).addEventListener("click", () => this.step(-1));
        (this.root.querySelector("[data-lb-next]") as HTMLElement).addEventListener("click", () => this.step(1));
        document.addEventListener("keydown", (e) => this.onKey(e));

        // Swipe on touch devices
        let startX = 0;
        this.root.addEventListener("touchstart", (e) => { startX = e.touches[0].clientX; }, { passive: true });
        this.root.addEventListener("touchend", (e) => {
            const dx = e.changedTouches[0].clientX - startX;
            if (Math.abs(dx) > 50) this.step(dx < 0 ? 1 : -1);
        });
    }

    open(photos: Photo[], index: number): void {
        this.photos = photos;
        this.index = index;
        this.lastFocus = document.activeElement;
        this.root.hidden = false;
        document.body.classList.add("lightbox-open");
        this.show();
        (this.root.querySelector("[data-lb-close]:not(.lightbox__backdrop)") as HTMLElement | null)?.focus();
    }

    close(): void {
        this.root.hidden = true;
        document.body.classList.remove("lightbox-open");
        this.img.src = "";
        if (this.lastFocus instanceof HTMLElement) this.lastFocus.focus();
    }

    private step(delta: number): void {
        if (this.root.hidden || !this.photos.length) return;
        this.index = (this.index + delta + this.photos.length) % this.photos.length;
        this.show();
    }

    private show(): void {
        const p = this.photos[this.index];
        if (!p) return;
        this.img.src = p.large;
        this.img.alt = p.caption || p.title;
        this.caption.textContent = p.caption || "";
        this.caption.hidden = !p.caption;
        this.counter.textContent = `${this.index + 1} / ${this.photos.length}`;
        this.download.href = p.original;
        // Preload neighbours for snappy navigation
        [1, -1].forEach((d) => {
            const n = this.photos[(this.index + d + this.photos.length) % this.photos.length];
            if (n) new Image().src = n.large;
        });
    }

    private onKey(e: KeyboardEvent): void {
        if (this.root.hidden) return;
        if (e.key === "Escape") this.close();
        else if (e.key === "ArrowRight") this.step(1);
        else if (e.key === "ArrowLeft") this.step(-1);
    }
}

let lightbox: Lightbox | null = null;
const getLightbox = (): Lightbox => (lightbox ??= new Lightbox());

// -----------------------------------------------------------------------------
// Gallery
// -----------------------------------------------------------------------------

const readPhoto = (a: HTMLAnchorElement): Photo => ({
    large: a.href,
    original: a.dataset.original || a.href,
    title: a.dataset.title || "",
    caption: a.dataset.caption || "",
});

function initOneGallery(gallery: HTMLElement): void {
    const grid = gallery.querySelector<HTMLElement>("[data-gallery-grid]");
    if (!grid) return;

    // Lightbox: delegate clicks so photos added later work too
    grid.addEventListener("click", (e) => {
        const a = (e.target as HTMLElement).closest<HTMLAnchorElement>("a[data-gallery-photo]");
        if (!a || !grid.contains(a)) return;
        e.preventDefault();
        const anchors = Array.from(grid.querySelectorAll<HTMLAnchorElement>("a[data-gallery-photo]"));
        getLightbox().open(anchors.map(readPhoto), anchors.indexOf(a));
    });

    // Load more
    const more = gallery.querySelector<HTMLButtonElement>("[data-gallery-more]");
    const url = gallery.dataset.itemsUrl;
    if (!more || !url) return;
    const remaining = more.querySelector<HTMLElement>("[data-gallery-remaining]");
    let busy = false;

    more.addEventListener("click", async () => {
        if (busy) return;
        busy = true;
        more.disabled = true;
        more.classList.add("is-loading");
        const offset = Number(more.dataset.offset || "0");
        try {
            const res = await fetch(`${url}?offset=${offset}`, {
                headers: { Accept: "application/json" },
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = (await res.json()) as ItemsResponse;
            grid.insertAdjacentHTML("beforeend", data.html);
            more.dataset.offset = String(data.next_offset);
            if (remaining) remaining.textContent = String(Math.max(0, data.total - data.next_offset));
            if (!data.has_more) more.closest(".gallery__more-wrap")?.remove();
        } catch (err) {
            console.error("Gallery: could not load more photos", err);
            more.textContent = "Could not load more photos — try again";
        } finally {
            busy = false;
            more.disabled = false;
            more.classList.remove("is-loading");
        }
    });
}

export function initGallery(): void {
    document.querySelectorAll<HTMLElement>("[data-gallery]").forEach(initOneGallery);
}
